import {
  ACTION_ATTRIBUTES,
  NO_ID_OBJECTS,
  VALID_ATTRIBUTES,
  type RuleAction,
  type RuleAttribute,
  type RuleObjectKeyword,
  type RulePremise,
  type RuleVariable,
} from "../engine/controlRules/controlRuleParser.ts";

/** A rule's structured shape while being edited visually; every edit immediately re-serializes it back into `ControlRule.body` text (`serializeControlRule`), which stays the single source of truth. */
export interface ControlRuleDraft {
  priority: number;
  premises: RulePremise[];
  thenActions: RuleAction[];
  elseActions: RuleAction[];
}

/**
 * Element names split by real kind, not one merged list — a merged list
 * previously let the id default to e.g. a CONDUIT's name even when the
 * object picker was set to PUMP, silently making the rule a no-op (the
 * engine never cross-checks an id's real kind against its keyword either).
 */
export interface ElementNames {
  node: string[];
  gage: string[];
  conduit: string[];
  pump: string[];
  orifice: string[];
  weir: string[];
  outlet: string[];
}

/** LINK is the one generic keyword and offers all 5 element kinds combined; SIMULATION has no id at all. */
export function idOptionsFor(object: RuleObjectKeyword, names: ElementNames): string[] {
  switch (object) {
    case "GAGE":
      return names.gage;
    case "NODE":
      return names.node;
    case "SIMULATION":
      return [];
    case "CONDUIT":
      return names.conduit;
    case "PUMP":
      return names.pump;
    case "ORIFICE":
      return names.orifice;
    case "WEIR":
      return names.weir;
    case "OUTLET":
      return names.outlet;
    case "LINK":
      return [...names.conduit, ...names.pump, ...names.orifice, ...names.weir, ...names.outlet];
  }
}

export function defaultVariableFor(
  object: RuleObjectKeyword,
  attributes: RuleAttribute[],
  names: ElementNames
): RuleVariable {
  const id = NO_ID_OBJECTS.has(object) ? "" : (idOptionsFor(object, names)[0] ?? "");
  return { object, id, attribute: attributes[0] };
}

export function defaultPremise(names: ElementNames): RulePremise {
  const attrs = Array.from(VALID_ATTRIBUTES.NODE ?? []);
  return {
    logicOp: "AND",
    lhs: defaultVariableFor("NODE", attrs, names),
    relop: ">",
    rhsLiteral: 0,
    rhsVar: null,
  };
}

export function defaultAction(names: ElementNames): RuleAction {
  return { target: defaultVariableFor("PUMP", ACTION_ATTRIBUTES.PUMP, names), value: 1 };
}

/** A brand-new rule starts with one premise and one THEN action rather than a blank form. */
export function defaultControlRuleDraft(names: ElementNames): ControlRuleDraft {
  return {
    priority: 0,
    premises: [defaultPremise(names)],
    thenActions: [defaultAction(names)],
    elseActions: [],
  };
}
